import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import { productDisplayName, boxLaneLabel } from "./productDisplay";
import type {
  NTXProductionDepartment,
  NTXForm,
  NTXProduct,
  NTXSalesOrder,
  NTXSalesOrderLine,
  NTXFormCapacity,
  NTXScheduleItem,
  CapacitySummary,
  OpenOrderRow,
  GroupedOpenOrder,
  CapacityRow,
  WorkloadPeriodEntry,
  ScheduleStatus,
  ItemTypeFilter,
  PublishFilters,
  PublishRow,
  ItemCriticality,
  ScheduleColumn,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyStore = any;

/** Default daily slots per form when no form_capacity row exists for a day. */
const DEFAULT_DAILY_SLOTS = 6;

/** Order-line priority at/above which an item is flagged critical (1–10 scale). */
const HIGH_PRIORITY_THRESHOLD = 8;

/** Stable palette assigned to forms (columns) in display order. */
const FORM_COLORS = [
  "#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c62828",
  "#0097a7", "#5d4037", "#455a64", "#00796b", "#ad1457",
];

/** Dedicated non-form lanes in the day grid (B.3). */
const CUSTOM_COLUMN_COLOR = "#6a1b9a";
const STANDARD_COLUMN_COLOR = "#00838f";
const MISC_COLUMN_COLOR = "#607d8b";

/**
 * Sortable size rank from a box/form label so standard lanes order ascending by
 * size (B.3): "3x3" → 3003, "4x4" → 4004, "6" → 6000, "48" → 48000. Unparseable
 * labels sort last. Per the backend fe-handoff, this is the size token Grant
 * wants the board organized by ("less called out as forms, just the box").
 */
const parseSizeRank = (label: string): number => {
  const m = (label || "").match(/(\d+)\s*(?:[xX×]\s*(\d+))?/);
  if (!m) return Number.POSITIVE_INFINITY;
  const a = parseInt(m[1], 10);
  const b = m[2] ? parseInt(m[2], 10) : 0;
  return a * 1000 + b;
};

/** Customer-friendly status labels for published (customer-facing) schedules. */
const PUBLISH_STATUS_LABELS: Record<ScheduleStatus, string> = {
  planned: "Scheduled",
  in_progress: "In Production",
  completed: "Completed",
  cancelled: "Cancelled",
};

class NTXProductionScheduleStore {
  rootStore: RootStore;

  // UI state
  currentTab = 0;
  dateRangeStart: string = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 10);
    return d.toISOString().split("T")[0];
  })();
  dateRangeEnd: string = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  })();
  /** Forms to show; empty = all forms. */
  selectedFormIds: string[] = [];
  /** Project/job to show; empty = all projects. */
  selectedProjectId = "";
  /** Foreman to show; empty = all foremen. Enables historical filtering by crew. */
  selectedForeman = "";
  searchQuery = "";
  itemTypeFilter: ItemTypeFilter = "all";
  /**
   * Critical-path rows the user has manually hidden (A.3) to limit which boxes
   * show. Session-only and additive on top of the attribute filters; cleared
   * when the date range changes (the visible set is then different).
   */
  hiddenCriticalPathIds = new Set<string>();
  scheduleZoom: "day" | "week" | "month" = "week";
  /**
   * Anchor date for the week view (C.1). Week zoom shows ONLY the Mon–Fri week
   * containing this date — not the full date range — so it reads like a single
   * week rather than a month. The prev/next arrows step it by ±7 days.
   */
  weekAnchor: string = (() => new Date().toISOString().split("T")[0])();
  /**
   * Bumped by goToToday() so the active view (Day/Week/Month) can scroll today
   * into view. The views watch this in an effect and call scrollIntoView — the
   * counter, not the date, is the trigger so clicking "Today" re-scrolls even
   * when the visible window already contains today.
   */
  todayScrollTick = 0;
  /**
   * Day-grid column grouping (C.2). "size" = the B.3 box-size board (Custom +
   * size-ordered form lanes + Misc); "type" = a simple two-lane Custom | Standard
   * split. Reads the existing per-item classification either way.
   */
  scheduleGroupBy: "size" | "type" = "size";
  workloadPeriod: "daily" | "weekly" | "monthly" = "daily";

  // Dialog state
  editingScheduleItem: NTXScheduleItem | null = null;
  pendingDeleteItem: NTXScheduleItem | null = null;
  /** Schedule item whose product QC checklist is shown in the Product Detail dialog. */
  detailItem: NTXScheduleItem | null = null;
  dialogPrefill: {
    productId?: string;
    formId?: string;
    quantity?: number;
    salesOrderLineId?: string;
  } | null = null;

  // Loading state
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    // Default zoom is week — constrain the initial window to the anchored week
    // so the header date picker and data filter agree from first paint (C.1).
    if (this.scheduleZoom === "week") this.syncRangeToWeek();
  }

  // ── Service store accessors ──

  private get _scheduleStore(): AnyStore {
    return (this.rootStore as AnyStore).productionSchedulesStore;
  }
  private get _orderStore(): AnyStore {
    return (this.rootStore as AnyStore).salesOrdersStore;
  }
  private get _orderLineStore(): AnyStore {
    return (this.rootStore as AnyStore).salesOrderLinesStore;
  }
  private get _productStore(): AnyStore {
    return (this.rootStore as AnyStore).productsStore;
  }
  private get _formStore(): AnyStore {
    return (this.rootStore as AnyStore).formsStore;
  }
  private get _deptStore(): AnyStore {
    return (this.rootStore as AnyStore).productionDepartmentsStore;
  }
  private get _capacityStore(): AnyStore {
    return (this.rootStore as AnyStore).formCapacityStore;
  }
  private get _projectStore(): AnyStore {
    return (this.rootStore as AnyStore).projectsStore;
  }

  // ── Computed: raw entity lists ──

  get scheduleItems(): NTXScheduleItem[] {
    const raw = (this._scheduleStore?.entities as NTXScheduleItem[]) || [];
    return raw.map((item) => {
      const needsDate = Boolean(item.scheduleDate?.includes("T"));
      const needsEnd = Boolean(item.endDate?.includes("T"));
      if (!needsDate && !needsEnd) return item;
      return {
        ...item,
        scheduleDate: needsDate ? item.scheduleDate.split("T")[0] : item.scheduleDate,
        endDate: needsEnd ? (item.endDate as string).split("T")[0] : item.endDate,
      };
    });
  }

  get salesOrders(): NTXSalesOrder[] {
    return (this._orderStore?.entities as NTXSalesOrder[]) || [];
  }

  get salesOrderLines(): NTXSalesOrderLine[] {
    return (this._orderLineStore?.entities as NTXSalesOrderLine[]) || [];
  }

  get products(): NTXProduct[] {
    return (this._productStore?.entities as NTXProduct[]) || [];
  }

  get forms(): NTXForm[] {
    return (this._formStore?.entities as NTXForm[]) || [];
  }

  get departments(): NTXProductionDepartment[] {
    return (this._deptStore?.entities as NTXProductionDepartment[]) || [];
  }

  get formCapacities(): NTXFormCapacity[] {
    return (this._capacityStore?.entities as NTXFormCapacity[]) || [];
  }

  get projects(): Array<{ id: string; name?: string; projectName?: string }> {
    return (
      (this._projectStore?.entities as Array<{
        id: string;
        name?: string;
        projectName?: string;
      }>) || []
    );
  }

  // ── Lookups ──

  getProduct = (productId?: string): NTXProduct | undefined =>
    productId ? this.products.find((p) => p.id === productId) : undefined;

  getForm = (formId?: string): NTXForm | undefined =>
    formId ? this.forms.find((f) => f.id === formId) : undefined;

  getLine = (lineId?: string): NTXSalesOrderLine | undefined =>
    lineId
      ? this.salesOrderLines.find((l) => (l.id ?? l.salesOrderLineId) === lineId)
      : undefined;

  getOrder = (orderId?: string): NTXSalesOrder | undefined =>
    orderId
      ? this.salesOrders.find((o) => (o.id ?? o.salesOrderId) === orderId)
      : undefined;

  /** Short structure reference (e.g. "SN 004") parsed from the linked line's notes. */
  structureRefOf = (item: NTXScheduleItem): string | undefined => {
    const notes = this.getLine(item.salesOrderLineId)?.notes;
    const match = notes?.match(/SN\s+[^·]+/i);
    return match ? match[0].trim() : undefined;
  };

  /**
   * Identify the project/job an order belongs to. Keyed by projectId when set,
   * else jobName, so filtering works whether or not a projects entity is seeded.
   * Label prefers the project entity's name, falling back to the job name.
   */
  private projectForOrder(order?: NTXSalesOrder): { key: string; label: string } {
    if (!order) return { key: "unassigned", label: "Unassigned" };
    const key = order.projectId || order.jobName || "unassigned";
    const proj = order.projectId
      ? this.projects.find((p) => p.id === order.projectId)
      : undefined;
    const label = proj?.projectName || proj?.name || order.jobName || "Unassigned";
    return { key, label };
  }

  /** {key,label} of the project/job a schedule item belongs to (via its order line). */
  projectOf = (item: NTXScheduleItem): { key: string; label: string } => {
    const line = this.getLine(item.salesOrderLineId);
    return this.projectForOrder(line ? this.getOrder(line.salesOrderId) : undefined);
  };

  /**
   * Distinct projects with scheduled work in the current date range — the
   * project filter's options (mirrors formOptions; not narrowed by selection).
   */
  get projectOptions(): Array<{ id: string; label: string }> {
    const map = new Map<string, string>();
    for (const si of this.scheduleItems) {
      if (si.scheduleDate <= this.dateRangeEnd && this.spanEnd(si) >= this.dateRangeStart) {
        const { key, label } = this.projectOf(si);
        if (!map.has(key)) map.set(key, label);
      }
    }
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Distinct foreman names already used — type-ahead suggestions (free-text stays free). */
  get foremanOptions(): string[] {
    const names = new Set<string>();
    for (const si of this.scheduleItems) {
      const name = si.foreman?.trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }

  /** Human label for the active project filter (for report headers). */
  get selectedProjectLabel(): string {
    if (!this.selectedProjectId) return "";
    return (
      this.projectOptions.find((p) => p.id === this.selectedProjectId)?.label ||
      this.selectedProjectId
    );
  }

  /**
   * Custom job structure vs. standard recurring item. Precedence: an explicit
   * line.isCustomItem (set via the schedule dialog) wins, then the product's
   * explicit itemType, then the NTX-PR-* code heuristic as a fallback (covers
   * Project Rise structures on environments where the flags aren't seeded).
   */
  isItemCustom = (product?: NTXProduct, line?: NTXSalesOrderLine): boolean => {
    // A linked line's explicit flag is the per-item override the schedule dialog
    // writes — authoritative in BOTH directions (true → custom, false → standard),
    // so flipping an item sticks even when its product's itemType / NTX-PR code
    // says otherwise (e.g. a seeded Rise item that's custom on both the line AND
    // the product). Only with no line flag do we fall back to the product's
    // itemType, then the NTX-PR-* heuristic (un-seeded environments).
    if (typeof line?.isCustomItem === "boolean") return line.isCustomItem;
    if (product?.itemType === "custom") return true;
    if (product?.itemType === "standard") return false; // explicit standard wins over the heuristic
    return Boolean(product?.productCode?.startsWith("NTX-PR-"));
  };

  getFormColor = (formId: string): string => {
    // Indexed against the unfiltered options so colors are stable under filtering.
    const idx = this.formOptions.findIndex((f) => f.id === formId);
    return FORM_COLORS[(idx < 0 ? 0 : idx) % FORM_COLORS.length];
  };

  /** Department all schedule items belong to (Storm Structures), used as a default. */
  get defaultDepartmentId(): string | undefined {
    const fromItem = this.scheduleItems.find((si) => si.departmentId)?.departmentId;
    return fromItem ?? this.departments[0]?.id;
  }

  // ── Date helpers ──

  private weekdaysBetween(start: string, end: string): string[] {
    const dates: string[] = [];
    const current = new Date(start + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    while (current <= last) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  /**
   * Today as YYYY-MM-DD, matching the ISO convention used by weekAnchor /
   * goToToday so the views can mark today's row/cell consistently.
   */
  get todayISO(): string {
    return new Date().toISOString().split("T")[0];
  }

  /** Monday of the anchored week (week zoom). */
  get weekStart(): string {
    const d = new Date(this.weekAnchor + "T00:00:00");
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // back up to Monday
    return d.toISOString().split("T")[0];
  }

  /** Friday of the anchored week (week zoom). */
  get weekEnd(): string {
    const d = new Date(this.weekStart + "T00:00:00");
    d.setDate(d.getDate() + 4);
    return d.toISOString().split("T")[0];
  }

  /**
   * True only when the zoomable Schedule tab is showing its single-week view.
   * Zoom (day/week/month) is a Schedule-tab control, so the week constraint must
   * NOT leak into other tabs — the Critical Path Gantt (tab 4) and the rest span
   * the full date range and rely on the header picker to widen them.
   */
  private get isScheduleWeekView(): boolean {
    return this.scheduleZoom === "week" && this.currentTab === 0;
  }

  get datesInRange(): string[] {
    // Week zoom is a single Mon–Fri week (C.1) — but only on the Schedule tab;
    // day/month, and every other tab, span the full range.
    if (this.isScheduleWeekView) {
      return this.weekdaysBetween(this.weekStart, this.weekEnd);
    }
    return this.weekdaysBetween(this.dateRangeStart, this.dateRangeEnd);
  }

  /** Date that is `days` weekdays after dateStr (skipping weekends). */
  addWeekdays = (dateStr: string, days: number): string => {
    const d = new Date(dateStr + "T00:00:00");
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d.toISOString().split("T")[0];
  };

  /** Weekdays an item occupies: [scheduleDate, endDate] inclusive (single date when no span). */
  spanDates = (item: NTXScheduleItem): string[] => {
    const end =
      item.endDate && item.endDate > item.scheduleDate ? item.endDate : item.scheduleDate;
    const dates = this.weekdaysBetween(item.scheduleDate, end);
    return dates.length > 0 ? dates : [item.scheduleDate];
  };

  /** Inclusive span end for an item (scheduleDate when single-day). */
  spanEnd = (item: NTXScheduleItem): string =>
    item.endDate && item.endDate > item.scheduleDate ? item.endDate : item.scheduleDate;

  /**
   * Critical-path callout metadata (B.2 v1): high-priority flag (for the "high
   * border" + badge) and an at-risk flag when an active item's span finishes
   * after the linked line's anticipation (need-by) date.
   */
  criticalityOf = (item: NTXScheduleItem): ItemCriticality => {
    const line = this.getLine(item.salesOrderLineId);
    const priority = line?.priority ?? 0;
    const anticipationDate = line?.anticipationDate || "";
    const active = item.status !== "completed" && item.status !== "cancelled";
    return {
      priority,
      isHighPriority: priority >= HIGH_PRIORITY_THRESHOLD,
      atRisk: active && Boolean(anticipationDate) && this.spanEnd(item) > anticipationDate,
      anticipationDate,
    };
  };

  // ── Schedule filtering ──

  get filteredScheduleItems(): NTXScheduleItem[] {
    // Span-aware: keep items whose [scheduleDate, endDate] overlaps the range.
    let items = this.scheduleItems.filter(
      (si) =>
        si.scheduleDate <= this.dateRangeEnd &&
        this.spanEnd(si) >= this.dateRangeStart
    );
    if (this.selectedFormIds.length > 0) {
      items = items.filter(
        (si) => si.formId && this.selectedFormIds.includes(si.formId)
      );
    }
    if (this.selectedProjectId) {
      items = items.filter((si) => this.projectOf(si).key === this.selectedProjectId);
    }
    if (this.selectedForeman) {
      items = items.filter((si) => (si.foreman?.trim() || "") === this.selectedForeman);
    }
    if (this.itemTypeFilter !== "all") {
      items = items.filter((si) => {
        const custom = this.isItemCustom(
          this.getProduct(si.productId),
          this.getLine(si.salesOrderLineId)
        );
        return this.itemTypeFilter === "custom" ? custom : !custom;
      });
    }
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      items = items.filter((si) => {
        const product = this.getProduct(si.productId);
        const line = this.getLine(si.salesOrderLineId);
        return (
          product?.productName?.toLowerCase().includes(q) ||
          product?.productCode?.toLowerCase().includes(q) ||
          productDisplayName(product).toLowerCase().includes(q) ||
          line?.notes?.toLowerCase().includes(q)
        );
      });
    }
    return items;
  }

  /**
   * Candidate forms for the filter dropdown — those used in the date range,
   * else all. NOT narrowed by the form selection (the menu must keep showing
   * deselected options), and the basis for stable form colors.
   */
  get formOptions(): NTXForm[] {
    const usedIds = new Set<string>();
    for (const si of this.scheduleItems) {
      if (
        si.scheduleDate <= this.dateRangeEnd &&
        this.spanEnd(si) >= this.dateRangeStart &&
        si.formId
      ) {
        usedIds.add(si.formId);
      }
    }
    const result =
      usedIds.size > 0
        ? this.forms.filter((f) => usedIds.has(f.id))
        : [...this.forms];
    return result.sort((a, b) =>
      (a.formName || "").localeCompare(b.formName || "")
    );
  }

  /** Forms shown as grid columns — formOptions narrowed by the multi-select. */
  get activeForms(): NTXForm[] {
    if (this.selectedFormIds.length === 0) return this.formOptions;
    return this.formOptions.filter((f) => this.selectedFormIds.includes(f.id));
  }

  /** Span-aware items per visible weekday, across all forms (calendar views). */
  get itemsByDate(): Map<string, NTXScheduleItem[]> {
    const map = new Map<string, NTXScheduleItem[]>();
    for (const date of this.datesInRange) map.set(date, []);
    for (const item of this.filteredScheduleItems) {
      for (const date of this.spanDates(item)) {
        map.get(date)?.push(item);
      }
    }
    return map;
  }

  // ── Grid: Map<date, Map<formId, items[]>> ──

  get scheduleGrid(): Map<string, Map<string, NTXScheduleItem[]>> {
    const grid = new Map<string, Map<string, NTXScheduleItem[]>>();
    for (const date of this.datesInRange) {
      const formMap = new Map<string, NTXScheduleItem[]>();
      for (const form of this.activeForms) formMap.set(form.id, []);
      grid.set(date, formMap);
    }
    for (const item of this.filteredScheduleItems) {
      // Span items occupy every weekday bucket in [scheduleDate, endDate];
      // days outside the visible range fall out naturally (no bucket).
      for (const date of this.spanDates(item)) {
        const formMap = grid.get(date);
        const bucket = item.formId ? formMap?.get(item.formId) : undefined;
        if (bucket) bucket.push(item);
      }
    }
    return grid;
  }

  /** Custom/standard classification for a scheduled item (resolves its product + line). */
  isScheduleItemCustom = (item: NTXScheduleItem): boolean =>
    this.isItemCustom(this.getProduct(item.productId), this.getLine(item.salesOrderLineId));

  // ── Day grid organized by box size (B.3) ──

  /**
   * Day-grid columns: a dedicated **Custom** lane (only when custom work is in
   * range), then **standard form lanes ascending by box size**, then a **Misc**
   * catch-all (only when standard items have no/ off-board form). This is the
   * "organize by size, not forms" board Grant asked for — customs (incl. the
   * form-less ones the old form-keyed grid dropped) collect in their own lane.
   */
  get scheduleColumns(): ScheduleColumn[] {
    const inRange = this.filteredScheduleItems;
    const hasCustom = inRange.some((si) => this.isScheduleItemCustom(si));

    // C.2 — collapse to a simple Custom | Standard split when grouping by type.
    if (this.scheduleGroupBy === "type") {
      const hasStandard = inRange.some((si) => !this.isScheduleItemCustom(si));
      const cols: ScheduleColumn[] = [];
      if (hasCustom) {
        cols.push({ key: "custom", label: "Custom", color: CUSTOM_COLUMN_COLOR, kind: "custom" });
      }
      if (hasStandard) {
        cols.push({ key: "standard", label: "Standard", color: STANDARD_COLUMN_COLOR, kind: "standard" });
      }
      return cols;
    }

    const formCols: ScheduleColumn[] = [...this.activeForms]
      .sort((a, b) => {
        const ra = parseSizeRank(a.formName);
        const rb = parseSizeRank(b.formName);
        if (ra !== rb) return ra - rb;
        return (a.formName || "").localeCompare(b.formName || "");
      })
      .map((f) => {
        // Lead with the box size, de-emphasize the form/structure type (B.3).
        const { size, type } = boxLaneLabel(f.formName);
        return {
          key: f.id,
          label: size,
          subLabel: type || undefined,
          color: this.getFormColor(f.id),
          kind: "form" as const,
          formId: f.id,
        };
      });

    const activeFormIds = new Set(this.activeForms.map((f) => f.id));
    const hasMisc = inRange.some(
      (si) =>
        !this.isScheduleItemCustom(si) && (!si.formId || !activeFormIds.has(si.formId))
    );

    const cols: ScheduleColumn[] = [];
    if (hasCustom) {
      cols.push({ key: "custom", label: "Custom", color: CUSTOM_COLUMN_COLOR, kind: "custom" });
    }
    cols.push(...formCols);
    if (hasMisc) {
      cols.push({ key: "misc", label: "Other", color: MISC_COLUMN_COLOR, kind: "misc" });
    }
    return cols;
  }

  /** Span-aware items per visible weekday, keyed by `scheduleColumns` key (B.3). */
  get scheduleGridByColumn(): Map<string, Map<string, NTXScheduleItem[]>> {
    const columns = this.scheduleColumns;
    const formKeys = new Set(
      columns.filter((c) => c.kind === "form").map((c) => c.key)
    );
    const grid = new Map<string, Map<string, NTXScheduleItem[]>>();
    for (const date of this.datesInRange) {
      const colMap = new Map<string, NTXScheduleItem[]>();
      for (const col of columns) colMap.set(col.key, []);
      grid.set(date, colMap);
    }
    const byType = this.scheduleGroupBy === "type";
    for (const item of this.filteredScheduleItems) {
      const custom = this.isScheduleItemCustom(item);
      const key = byType
        ? custom
          ? "custom"
          : "standard"
        : custom
          ? "custom"
          : item.formId && formKeys.has(item.formId)
            ? item.formId
            : "misc";
      for (const date of this.spanDates(item)) {
        grid.get(date)?.get(key)?.push(item);
      }
    }
    return grid;
  }

  /** Available daily slots for a form on a date (form_capacity, else default). */
  private availableFor(formId: string, date: string): number {
    const cap = this.formCapacities.find(
      (c) => c.formId === formId && (c.date || "").split("T")[0] === date
    );
    return cap?.availableCapacity ?? DEFAULT_DAILY_SLOTS;
  }

  get capacityByFormDate(): Map<string, Map<string, CapacitySummary>> {
    const result = new Map<string, Map<string, CapacitySummary>>();
    for (const date of this.datesInRange) {
      const formMap = new Map<string, CapacitySummary>();
      for (const form of this.activeForms) {
        const items = this.scheduleGrid.get(date)?.get(form.id) || [];
        const scheduled = items
          .filter((si) => si.status !== "cancelled")
          .reduce((sum, si) => sum + (si.quantity || 0), 0);
        const available = this.availableFor(form.id, date);
        formMap.set(form.id, {
          scheduled,
          available,
          percentage: available > 0 ? Math.round((scheduled / available) * 100) : 0,
        });
      }
      result.set(date, formMap);
    }
    return result;
  }

  // ── Open orders (unscheduled / partially scheduled lines) ──

  /** Quantity already scheduled per sales order line (non-cancelled items). */
  private get scheduledByLine(): Map<string, number> {
    const map = new Map<string, number>();
    for (const si of this.scheduleItems) {
      if (si.status === "cancelled" || !si.salesOrderLineId) continue;
      map.set(
        si.salesOrderLineId,
        (map.get(si.salesOrderLineId) || 0) + (si.quantity || 0)
      );
    }
    return map;
  }

  get openOrders(): OpenOrderRow[] {
    return this.salesOrderLines
      .filter((l) => l.status !== "dropped" && l.status !== "completed")
      .map((line) => {
        const lineId = line.id ?? line.salesOrderLineId ?? "";
        const order = this.salesOrders.find(
          (o) => (o.id ?? o.salesOrderId) === line.salesOrderId
        );
        const product = this.getProduct(line.productId);
        const formId = line.assignedFormId || product?.formId || "";
        const form = this.getForm(formId);
        const scheduled = this.scheduledByLine.get(lineId) || 0;
        const qty = line.quantity || 0;
        return {
          lineId,
          orderNumber: order?.orderNumber || "",
          jobName: order?.jobName || "",
          productId: line.productId,
          productCode: product?.productCode || "",
          productName: product?.productName || "",
          productDisplayName: productDisplayName(product),
          formId,
          formName: form?.formName || "",
          quantity: qty,
          scheduledQuantity: scheduled,
          remainingQuantity: qty - scheduled,
          priority: line.priority ?? 0,
          anticipationDate: line.anticipationDate || "",
          status: line.status,
          notes: line.notes || "",
          isCustom: this.isItemCustom(product, line),
          projectKey: this.projectForOrder(order).key,
        };
      })
      .filter((row) => row.remainingQuantity > 0)
      .filter((row) => !this.selectedProjectId || row.projectKey === this.selectedProjectId)
      .filter(
        (row) =>
          this.itemTypeFilter === "all" ||
          (this.itemTypeFilter === "custom" ? row.isCustom : !row.isCustom)
      )
      .sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority; // higher first
        return (a.anticipationDate || "").localeCompare(b.anticipationDate || "");
      });
  }

  get groupedOpenOrders(): GroupedOpenOrder[] {
    const map = new Map<string, GroupedOpenOrder>();
    for (const row of this.openOrders) {
      const existing = map.get(row.orderNumber);
      if (existing) {
        existing.lines.push(row);
        existing.totalQuantity += row.quantity;
        existing.totalRemaining += row.remainingQuantity;
        if (row.priority > existing.priority) existing.priority = row.priority;
      } else {
        const order = this.salesOrders.find((o) => o.orderNumber === row.orderNumber);
        map.set(row.orderNumber, {
          salesOrderId: (order?.id ?? order?.salesOrderId) || "",
          orderNumber: row.orderNumber,
          jobName: row.jobName,
          priority: row.priority,
          anticipationDate: row.anticipationDate,
          lines: [row],
          totalQuantity: row.quantity,
          totalRemaining: row.remainingQuantity,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return (a.anticipationDate || "").localeCompare(b.anticipationDate || "");
    });
  }

  // ── Capacity tab rows ──

  get capacityRows(): CapacityRow[] {
    const rows: CapacityRow[] = [];
    for (const date of this.datesInRange) {
      for (const form of this.activeForms) {
        const summary = this.capacityByFormDate.get(date)?.get(form.id);
        if (!summary) continue;
        if (summary.scheduled === 0 && summary.available === DEFAULT_DAILY_SLOTS) continue;
        rows.push({
          formId: form.id,
          formName: form.formName,
          color: this.getFormColor(form.id),
          date,
          scheduled: summary.scheduled,
          available: summary.available,
          remaining: Math.max(0, summary.available - summary.scheduled),
          percentage: summary.percentage,
        });
      }
    }
    return rows.sort((a, b) =>
      a.date.localeCompare(b.date) || a.formName.localeCompare(b.formName)
    );
  }

  // ── Workload over time (per form, grouped by period) ──

  get workloadOverTime(): WorkloadPeriodEntry[] {
    const dates = this.datesInRange;
    if (dates.length === 0) return [];

    const isoWeek = (dateStr: string): number => {
      const d = new Date(dateStr + "T00:00:00");
      d.setDate(d.getDate() + 4 - (d.getDay() || 7));
      const yearStart = new Date(d.getFullYear(), 0, 1);
      return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    };

    const grouped = new Map<string, string[]>();
    for (const date of dates) {
      let key: string;
      if (this.workloadPeriod === "daily") {
        key = date;
      } else if (this.workloadPeriod === "weekly") {
        const d = new Date(date + "T00:00:00");
        key = `W${isoWeek(date)} ${d.getFullYear()}`;
      } else {
        const d = new Date(date + "T00:00:00");
        key = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      }
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(date);
    }

    const entries: WorkloadPeriodEntry[] = [];
    for (const [periodLabel, periodDates] of grouped) {
      const forms = this.activeForms.map((form) => {
        let scheduled = 0;
        let available = 0;
        for (const date of periodDates) {
          const summary = this.capacityByFormDate.get(date)?.get(form.id);
          scheduled += summary?.scheduled || 0;
          available += summary?.available || 0;
        }
        return {
          formId: form.id,
          formName: form.formName,
          color: this.getFormColor(form.id),
          scheduled,
          available,
        };
      });
      entries.push({ periodLabel, forms });
    }
    return entries;
  }

  // ── Customer-facing schedule publishing (2.2) ──

  /** A short, formatted production date or span for a published row. */
  private publishDateLabel(item: NTXScheduleItem): string {
    const start = new Date(item.scheduleDate + "T00:00:00");
    const startLabel = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const end = this.spanEnd(item);
    if (end === item.scheduleDate) return startLabel;
    const endLabel = new Date(end + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${startLabel} – ${endLabel}`;
  }

  /** Sales orders with at least one scheduled item — the publish dialog's order filter. */
  get publishableSalesOrders(): NTXSalesOrder[] {
    const orderIds = new Set<string>();
    for (const si of this.scheduleItems) {
      const line = this.getLine(si.salesOrderLineId);
      if (line?.salesOrderId) orderIds.add(line.salesOrderId);
    }
    return this.salesOrders
      .filter((o) => orderIds.has(o.id ?? o.salesOrderId ?? ""))
      .sort((a, b) => (a.orderNumber || "").localeCompare(b.orderNumber || ""));
  }

  /**
   * Customer-presentable delivery schedule rows for the given filters. Excludes
   * cancelled items and all internal data (capacity, priority, the internal
   * `notes`); the only note surfaced is the customer-facing `customerNotes`
   * (D.2). Sorted by production date, then order number.
   */
  publishRows = (filters: PublishFilters): PublishRow[] => {
    const { startDate, endDate, salesOrderId, itemType } = filters;
    return this.scheduleItems
      .filter((si) => si.status !== "cancelled")
      .filter((si) => si.scheduleDate <= endDate && this.spanEnd(si) >= startDate)
      .filter((si) => {
        if (!salesOrderId) return true;
        return this.getLine(si.salesOrderLineId)?.salesOrderId === salesOrderId;
      })
      .filter((si) => {
        if (itemType === "all") return true;
        const custom = this.isItemCustom(
          this.getProduct(si.productId),
          this.getLine(si.salesOrderLineId)
        );
        return itemType === "custom" ? custom : !custom;
      })
      .map((si) => {
        const product = this.getProduct(si.productId);
        const line = this.getLine(si.salesOrderLineId);
        const order = line
          ? this.salesOrders.find((o) => (o.id ?? o.salesOrderId) === line.salesOrderId)
          : undefined;
        return {
          scheduleDate: si.scheduleDate,
          endDate: si.endDate ?? null,
          dateLabel: this.publishDateLabel(si),
          orderNumber: order?.orderNumber || "",
          jobName: order?.jobName || "",
          structure: productDisplayName(product) || si.productId,
          structureNumber: this.structureRefOf(si) || "",
          // D.2 — the customer-facing note (never the internal `notes`).
          customerNote: si.customerNotes?.trim() ? si.customerNotes.trim() : "",
          productName: product?.productName || "",
          quantity: si.quantity || 0,
          status: si.status,
          statusLabel: PUBLISH_STATUS_LABELS[si.status] || si.status,
        };
      })
      .sort(
        (a, b) =>
          a.scheduleDate.localeCompare(b.scheduleDate) ||
          a.orderNumber.localeCompare(b.orderNumber)
      );
  };

  // ── Summary metrics ──

  get totalScheduledItems(): number {
    return this.filteredScheduleItems.filter((si) => si.status !== "cancelled").length;
  }

  get totalScheduledUnits(): number {
    return this.filteredScheduleItems
      .filter((si) => si.status !== "cancelled")
      .reduce((sum, si) => sum + (si.quantity || 0), 0);
  }

  get overCapacityCount(): number {
    let count = 0;
    for (const [, formMap] of this.capacityByFormDate) {
      for (const [, summary] of formMap) {
        if (summary.percentage > 100) count++;
      }
    }
    return count;
  }

  /** Visible items whose span finishes after their anticipation date (B.2). */
  get atRiskCount(): number {
    return this.filteredScheduleItems.filter((si) => this.criticalityOf(si).atRisk).length;
  }

  /**
   * Visible items ordered for the critical-path timeline (B.1): at-risk first,
   * then by priority (desc), then earliest start — so the most urgent spans sit
   * at the top of the Gantt and clusters/collisions read top-down.
   */
  get criticalPathItems(): NTXScheduleItem[] {
    return [...this.filteredScheduleItems].sort((a, b) => {
      const ca = this.criticalityOf(a);
      const cb = this.criticalityOf(b);
      if (ca.atRisk !== cb.atRisk) return ca.atRisk ? -1 : 1;
      if (cb.priority !== ca.priority) return cb.priority - ca.priority;
      return a.scheduleDate.localeCompare(b.scheduleDate);
    });
  }

  /** Critical-path items after applying the manual hide set (A.3). */
  get visibleCriticalPathItems(): NTXScheduleItem[] {
    if (this.hiddenCriticalPathIds.size === 0) return this.criticalPathItems;
    return this.criticalPathItems.filter((i) => !this.hiddenCriticalPathIds.has(i.id));
  }

  /** How many of the currently in-range critical-path items are manually hidden. */
  get hiddenCriticalPathCount(): number {
    return this.criticalPathItems.filter((i) =>
      this.hiddenCriticalPathIds.has(i.id)
    ).length;
  }

  /** The in-range critical-path items currently hidden, kept in urgency order (B.5). */
  get hiddenCriticalPathItems(): NTXScheduleItem[] {
    if (this.hiddenCriticalPathIds.size === 0) return [];
    return this.criticalPathItems.filter((i) => this.hiddenCriticalPathIds.has(i.id));
  }

  // ── Actions: UI ──

  setCurrentTab = (tab: number) => { this.currentTab = tab; };
  setDateRangeStart = (date: string) => {
    // On the Schedule tab's week view the visible window IS a single week, so the
    // picker acts as a "jump to the week containing this date" control (keeps
    // arrows + picker in sync). Everywhere else — day/month zoom, and other tabs
    // like the Critical Path Gantt — it sets the literal range bound.
    if (this.isScheduleWeekView) {
      this.weekAnchor = date;
      this.syncRangeToWeek();
    } else {
      this.dateRangeStart = date;
    }
    this.hiddenCriticalPathIds.clear();
  };
  setDateRangeEnd = (date: string) => {
    if (this.isScheduleWeekView) {
      this.weekAnchor = date;
      this.syncRangeToWeek();
    } else {
      this.dateRangeEnd = date;
    }
    this.hiddenCriticalPathIds.clear();
  };
  setSelectedFormIds = (formIds: string[]) => { this.selectedFormIds = formIds; };
  setSelectedProjectId = (projectId: string) => { this.selectedProjectId = projectId; };
  setSelectedForeman = (foreman: string) => { this.selectedForeman = foreman; };
  setSearchQuery = (query: string) => { this.searchQuery = query; };
  setItemTypeFilter = (filter: ItemTypeFilter) => { this.itemTypeFilter = filter; };
  setScheduleZoom = (zoom: "day" | "week" | "month") => {
    if (zoom === "week") {
      // Entering week zoom: anchor on today, clamped into the current window so
      // the single visible week lands near whatever the user was looking at,
      // then constrain the window (and header picker) to that week.
      const today = new Date().toISOString().split("T")[0];
      this.weekAnchor =
        today < this.dateRangeStart
          ? this.dateRangeStart
          : today > this.dateRangeEnd
            ? this.dateRangeEnd
            : today;
      this.syncRangeToWeek();
    } else if (this.scheduleZoom === "week") {
      // Leaving week zoom: day/month are wide scrollable views, so re-expand the
      // window around the week the user was on instead of staying at 5 days.
      this.broadWindowAround(this.weekAnchor);
    }
    this.scheduleZoom = zoom;
  };
  setScheduleGroupBy = (groupBy: "size" | "type") => { this.scheduleGroupBy = groupBy; };

  /** Shift a YYYY-MM-DD by a day/week/month step (Teams-calendar navigation). */
  private shiftDate = (dateStr: string, unit: "day" | "week" | "month", dir: number): string => {
    const d = new Date(dateStr + "T00:00:00");
    if (unit === "day") d.setDate(d.getDate() + dir);
    else if (unit === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    return d.toISOString().split("T")[0];
  };

  /**
   * Week zoom's single source of truth (C.1): mirror the anchored Mon–Fri week
   * into dateRangeStart/End. Since the data filter (filteredScheduleItems) and
   * the header date picker both read the range, this keeps the columns, the
   * pulled data, and the picker in lockstep with the prev/next arrows.
   */
  private syncRangeToWeek = () => {
    this.dateRangeStart = this.weekStart;
    this.dateRangeEnd = this.weekEnd;
  };

  /** Reset to the default wide scroll window (today −10 / +30) around a date. */
  private broadWindowAround = (dateStr: string) => {
    this.dateRangeStart = this.shiftDate(dateStr, "day", -10);
    this.dateRangeEnd = this.shiftDate(dateStr, "day", 30);
  };

  /**
   * Teams-style range navigation (C.1) — hop the visible window forward/back by
   * the active zoom unit, preserving its width. Clears the manual hide set like
   * the date-range setters (the visible set changes).
   */
  stepRange = (unit: "day" | "week" | "month", dir: 1 | -1) => {
    if (unit === "week") {
      // Week zoom shows a single anchored week — hop the anchor, then mirror it
      // into the range so the header picker + data pull follow the arrows.
      this.weekAnchor = this.shiftDate(this.weekAnchor, "week", dir);
      this.syncRangeToWeek();
    } else {
      this.dateRangeStart = this.shiftDate(this.dateRangeStart, unit, dir);
      this.dateRangeEnd = this.shiftDate(this.dateRangeEnd, unit, dir);
    }
    this.hiddenCriticalPathIds.clear();
  };

  /** Snap the visible window back to today (C.1). */
  goToToday = () => {
    this.weekAnchor = new Date().toISOString().split("T")[0];
    // Week zoom → today's week; day/month → the wide scroll window.
    if (this.scheduleZoom === "week") {
      this.syncRangeToWeek();
    } else {
      this.broadWindowAround(this.weekAnchor);
    }
    this.hiddenCriticalPathIds.clear();
    // Signal the active view to scroll today into view (Day grid + Month
    // calendar scroll; Week already shows the single anchored week).
    this.todayScrollTick += 1;
  };
  setWorkloadPeriod = (period: "daily" | "weekly" | "monthly") => {
    this.workloadPeriod = period;
  };

  setEditingScheduleItem = (item: NTXScheduleItem | null) => {
    this.editingScheduleItem = item;
  };

  // ── Critical-path manual hide (A.3) ──

  /** Hide a single critical-path row. */
  hideCriticalPathItem = (itemId: string) => {
    this.hiddenCriticalPathIds.add(itemId);
  };

  /** Un-hide a single critical-path row. */
  showCriticalPathItem = (itemId: string) => {
    this.hiddenCriticalPathIds.delete(itemId);
  };

  /** Hide several critical-path rows at once (multi-select, B.5). */
  hideCriticalPathItems = (itemIds: string[]) => {
    itemIds.forEach((id) => this.hiddenCriticalPathIds.add(id));
  };

  /** Un-hide several critical-path rows at once (B.5). */
  showCriticalPathItems = (itemIds: string[]) => {
    itemIds.forEach((id) => this.hiddenCriticalPathIds.delete(id));
  };

  /** Clear the manual hide set so every in-range row shows again. */
  showAllCriticalPath = () => {
    this.hiddenCriticalPathIds.clear();
  };

  openDialogForOrderLine = (row: OpenOrderRow) => {
    this.editingScheduleItem = null;
    this.dialogPrefill = {
      productId: row.productId,
      formId: row.formId,
      quantity: row.remainingQuantity > 0 ? row.remainingQuantity : row.quantity,
      salesOrderLineId: row.lineId,
    };
    this.rootStore.routerStore.openDialog({ dialogName: "scheduleStructure" });
  };

  openScheduleDialog = (item?: NTXScheduleItem) => {
    this.editingScheduleItem = item || null;
    this.dialogPrefill = null;
    this.rootStore.routerStore.openDialog({ dialogName: "scheduleStructure" });
  };

  closeDialog = () => {
    this.editingScheduleItem = null;
    this.dialogPrefill = null;
    this.pendingDeleteItem = null;
    this.detailItem = null;
    this.rootStore.routerStore.closeDialog();
  };

  /** Open the Product Detail dialog (QC checklist preview) for a schedule item. */
  openProductDetail = (item: NTXScheduleItem) => {
    this.detailItem = item;
    this.rootStore.routerStore.openDialog({ dialogName: "productDetail" });
  };

  /** Open the delete-confirmation dialog for a scheduled item. */
  requestRemoveScheduleItem = (item: NTXScheduleItem) => {
    this.pendingDeleteItem = item;
    this.rootStore.routerStore.openDialog({ dialogName: "confirmDeleteScheduleItem" });
  };

  /** Confirm the pending delete and close the dialog. */
  confirmRemoveScheduleItem = async () => {
    const item = this.pendingDeleteItem;
    if (!item) return;
    await this.removeScheduleItem(item.id);
    this.closeDialog();
  };

  /** Pre/post-pour QC checklist opt-in for a product (false when unset). */
  productQc = (productId?: string): { prePour: boolean; postPour: boolean } => {
    const product = this.getProduct(productId);
    return {
      prePour: product?.qcPrePour === true,
      postPour: product?.qcPostPour === true,
    };
  };

  /** Toggle a product's pre/post-pour QC checklist opt-in (applies to all its items). */
  setProductQcChecklists = async (
    productId: string,
    flags: { prePour: boolean; postPour: boolean }
  ) => {
    if (!productId || !this._productStore) return;
    try {
      await this._productStore.patch(productId, {
        qcPrePour: flags.prePour,
        qcPostPour: flags.postPour,
      });
    } catch (err) {
      console.error("[NTX] failed to update product QC checklists:", err);
    }
  };

  /**
   * Set the custom/standard classification for a scheduled item's linked sales
   * order line (A.2). The schedule and critical-path views read this through
   * isItemCustom, which prefers an explicit line.isCustomItem over the NTX-PR-*
   * heuristic — so setting it here makes the Item Type filter and critical-path
   * filter reflect Grant's choice rather than the fallback guess.
   *
   * Backend: sales_order_lines.isCustomItem exists (core migration) and is in
   * the patch schema, so this PATCH persists. Still wrapped in try/catch so any
   * transport/hook error degrades gracefully rather than blocking scheduling.
   */
  setLineCustom = async (salesOrderLineId: string, isCustom: boolean) => {
    if (!salesOrderLineId || !this._orderLineStore) return;
    try {
      await this._orderLineStore.patch(salesOrderLineId, { isCustomItem: isCustom });
    } catch (err) {
      console.error("[NTX] failed to update sales order line custom flag:", err);
    }
  };

  /**
   * Set the custom/standard classification at the product level (A.2) — used for
   * items with no linked order line, where there's no line flag to write. Mirrors
   * setProductQcChecklists; isItemCustom reads product.itemType after the line flag.
   */
  setProductCustom = async (productId: string, isCustom: boolean) => {
    if (!productId || !this._productStore) return;
    try {
      await this._productStore.patch(productId, {
        itemType: isCustom ? "custom" : "standard",
      });
    } catch (err) {
      console.error("[NTX] failed to update product itemType:", err);
    }
  };

  // ── Actions: CRUD ──

  /**
   * Create (or reuse) a product for a typed-in structure name — A.1, the
   * typeable structure field. Lets Grant schedule one-off custom boxes before a
   * managed catalog exists: the typed name becomes a reusable NTX product so the
   * existing product-keyed pipeline (cards, critical path, print) keeps working
   * unchanged. Idempotent — typing a name that already exists reuses it rather
   * than duplicating; new entries are marked `itemType: 'custom'` (and carry an
   * NTX-PR-* code) so both the backend product flag and the FE classification
   * (isItemCustom) treat them as custom. The backend create path defaults
   * itemType to 'standard', so passing it explicitly is required (backend BG.1).
   * Returns the resolved product id, or "" on failure.
   */
  createCustomProduct = async (
    name: string,
    formId?: string,
    isCustom = true
  ): Promise<string> => {
    const trimmed = name.trim();
    if (!trimmed || !this._productStore) return "";

    // Reuse an existing product with the same name/short name (idempotent).
    const existing = this.products.find(
      (p) =>
        p.productName?.trim().toLowerCase() === trimmed.toLowerCase() ||
        p.shortName?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) return existing.id;

    const slug = trimmed
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 24);
    const productCode = `NTX-PR-${slug || "CUSTOM"}-${String(Date.now()).slice(-4)}`;
    const payload: Record<string, unknown> = {
      productCode,
      productName: trimmed,
      shortName: trimmed,
      itemType: isCustom ? "custom" : "standard",
      // Keep typed structures inside the NTX catalog scope (productType=storm_structure),
      // so they surface in the standard/custom catalog (B.7) and bucket on the board (B.3).
      // Per backend fe-handoff (2026-06-22).
      productType: "storm_structure",
    };
    if (formId) payload.formId = formId;
    if (this.defaultDepartmentId) payload.departmentId = this.defaultDepartmentId;

    try {
      const created = (await this._productStore.create(payload)) as
        | { id?: string }
        | undefined;
      await this.fetchAll();
      return (
        created?.id ||
        this.products.find((p) => p.productCode === productCode)?.id ||
        ""
      );
    } catch (err) {
      console.error("[NTX] failed to create custom product:", err);
      return "";
    }
  };

  scheduleStructure = async (data: {
    scheduleDate: string;
    endDate?: string | null;
    productId: string;
    formId: string;
    quantity: number;
    foreman?: string | null;
    notes?: string | null;
    customerNotes?: string | null;
    salesOrderLineId?: string;
  }) => {
    const line = this.getLine(data.salesOrderLineId);
    const newItem: Record<string, unknown> = {
      scheduleDate: data.scheduleDate.split("T")[0],
      endDate: data.endDate ? data.endDate.split("T")[0] : null,
      productId: data.productId,
      quantity: data.quantity,
      foreman: data.foreman?.trim() ? data.foreman.trim() : null,
      // B.2 note persists on the schedule item itself (production_schedules.notes)
      // — works for line-less customs and keeps the SN token in the line untouched.
      notes: data.notes?.trim() ? data.notes.trim() : null,
      // D.3 customer-facing note — kept distinct from the internal note above so
      // it can be surfaced on the published customer schedule (D.2).
      customerNotes: data.customerNotes?.trim() ? data.customerNotes.trim() : null,
      status: "planned",
    };
    if (data.formId) newItem.formId = data.formId;
    if (data.salesOrderLineId) newItem.salesOrderLineId = data.salesOrderLineId;
    if (this.defaultDepartmentId) newItem.departmentId = this.defaultDepartmentId;

    if (this._scheduleStore) {
      await this._scheduleStore.create(newItem);
    }

    // Mark the linked sales order line scheduled once fully covered.
    if (data.salesOrderLineId && line && this._orderLineStore) {
      const alreadyScheduled = this.scheduledByLine.get(data.salesOrderLineId) || 0;
      const newScheduled = alreadyScheduled + data.quantity;
      try {
        await this._orderLineStore.patch(data.salesOrderLineId, {
          scheduleDate: data.scheduleDate.split("T")[0],
          scheduledDate: data.scheduleDate.split("T")[0],
          assignedFormId: data.formId || line.assignedFormId,
          status: newScheduled >= (line.quantity || 0) ? "scheduled" : line.status,
        });
      } catch (err) {
        console.error("[NTX] failed to update sales order line:", err);
      }
    }

    await this.fetchAll();
    this.closeDialog();
  };

  updateScheduleItem = async (
    itemId: string,
    data: {
      scheduleDate: string;
      endDate?: string | null;
      productId: string;
      formId: string;
      quantity: number;
      foreman?: string | null;
      notes?: string | null;
      customerNotes?: string | null;
      status: ScheduleStatus;
    }
  ) => {
    const patch: Record<string, unknown> = {
      scheduleDate: data.scheduleDate.split("T")[0],
      endDate: data.endDate ? data.endDate.split("T")[0] : null,
      productId: data.productId,
      quantity: data.quantity,
      foreman: data.foreman?.trim() ? data.foreman.trim() : null,
      notes: data.notes?.trim() ? data.notes.trim() : null,
      customerNotes: data.customerNotes?.trim() ? data.customerNotes.trim() : null,
      status: data.status,
    };
    if (data.formId) patch.formId = data.formId;
    if (this._scheduleStore) {
      await this._scheduleStore.patch(itemId, patch);
    }
    await this.fetchAll();
    this.closeDialog();
  };

  removeScheduleItem = async (itemId: string) => {
    const item = this.scheduleItems.find((si) => si.id === itemId);
    if (!item) return;
    if (this._scheduleStore) {
      await this._scheduleStore.remove(itemId);
    }
    // Re-open the linked line so it shows back up in Open Orders.
    if (item.salesOrderLineId && this._orderLineStore) {
      const line = this.getLine(item.salesOrderLineId);
      if (line && line.status === "scheduled") {
        try {
          await this._orderLineStore.patch(item.salesOrderLineId, { status: "pending" });
        } catch (err) {
          console.error("[NTX] failed to revert sales order line:", err);
        }
      }
    }
    await this.fetchAll();
  };

  /** Items a push would move: on/after fromDate, not completed/cancelled, optional form. */
  private pushTargets(formId: string | null, fromDate: string): NTXScheduleItem[] {
    return this.scheduleItems.filter((si) => {
      if (si.scheduleDate < fromDate) return false;
      if (si.status === "cancelled" || si.status === "completed") return false;
      if (formId && si.formId !== formId) return false;
      return true;
    });
  }

  countPushTargets = (formId: string | null, fromDate: string): number =>
    fromDate ? this.pushTargets(formId, fromDate).length : 0;

  pushScheduleItems = async (params: {
    formId: string | null;
    fromDate: string;
    pushDays: number;
  }) => {
    const { formId, fromDate, pushDays } = params;
    if (pushDays <= 0) return;

    const matching = this.pushTargets(formId, fromDate);

    for (const item of matching) {
      // Spans move intact: start and end shift by the same number of weekdays.
      const patch: Record<string, unknown> = {
        scheduleDate: this.addWeekdays(item.scheduleDate, pushDays),
      };
      if (item.endDate) {
        patch.endDate = this.addWeekdays(item.endDate, pushDays);
      }
      if (this._scheduleStore) {
        await this._scheduleStore.patch(item.id, patch);
      }
    }
    await this.fetchAll();
  };

  // ── Data loading ──

  /**
   * Remove local entities the server no longer returns. The backend seed writes
   * straight to the DB (no feathers events), so re-seeding leaves stale rows in
   * the socket/localStorage cache — patching one of those yields NotFound and
   * the item vanishes. Only reconciles when the find returned the complete set.
   */
  private pruneStale(store: AnyStore, response: unknown) {
    const resp = response as
      | { data?: Array<Record<string, unknown>>; total?: number }
      | null
      | undefined;
    const data = resp?.data;
    if (!store || !Array.isArray(data)) return;
    if (typeof resp?.total === "number" && resp.total > data.length) return; // partial page
    const serverIds = new Set(data.map((r) => String(r.id)));
    const stale = (store.entities as Array<{ id?: string }>).filter(
      (e) => e.id && !serverIds.has(String(e.id))
    );
    for (const e of stale) store.removeEvent(e.id);
  }

  private fetchAll = async () => {
    const stores = [
      this._scheduleStore, this._orderStore, this._orderLineStore,
      this._productStore, this._formStore, this._deptStore, this._capacityStore,
    ];
    const results = await Promise.allSettled([
      this._scheduleStore?.find({
        query: { $limit: 1000, status: { $ne: "cancelled" }, $sort: { scheduleDate: 1 } },
      }),
      this._orderStore?.find({ query: { $limit: 500, status: { $nin: ["cancelled"] } } }),
      this._orderLineStore?.find({ query: { $limit: 2000, status: { $nin: ["dropped"] } } }),
      this._productStore?.find({ query: { $limit: 2000 } }),
      this._formStore?.find({ query: { $limit: 200 } }),
      this._deptStore?.find({ query: { $limit: 100 } }),
      this._capacityStore?.find({ query: { $limit: 2000 } }),
    ]);
    const names = [
      "productionSchedules", "salesOrders", "salesOrderLines",
      "products", "forms", "productionDepartments", "formCapacity",
    ];
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        console.warn(`[NTX] ${names[i]} find() failed:`, r.reason);
      } else {
        this.pruneStale(stores[i], r.value);
      }
    });
  };

  loadData = async () => {
    this.loading = true;
    this.error = null;
    try {
      await this.fetchAll();
      runInAction(() => { this.loading = false; });
    } catch (err: unknown) {
      console.error("[NTX] loadData failed:", err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load schedule data";
        this.loading = false;
      });
    }
  };
}

export default NTXProductionScheduleStore;
